/**
 * ParserRegistry — holds registered parsers and selects the best one for a job.
 */

import { Effect } from 'effect';

import type { Parser, ParserSpec, ParserCategory, ParserTier, InputPort } from './parser';
import type { ParserHookName, HookContext } from './hooks';

export class ParserRegistry {
  private parsers = new Map<string, Parser>();

  register(parser: Parser): void {
    this.parsers.set(parser.spec.id, parser);
  }

  unregister(id: string): boolean {
    return this.parsers.delete(id);
  }

  get(id: string): Parser | undefined {
    return this.parsers.get(id);
  }

  /** All parsers that pass validate() for this env. */
  getAvailable(env: Record<string, unknown>): Parser[] {
    return [...this.parsers.values()].filter((p) => p.validate(env));
  }

  /** Cascade: best parser for a category, excluding failures. */
  select(
    env: Record<string, unknown>,
    opts: {
      category: ParserCategory;
      tier?: ParserTier;
      exclude?: string[];
      inputPorts?: InputPort[];
    },
  ): Parser | null {
    const available = this.getAvailable(env).filter((p) => {
      if (p.spec.category !== opts.category) return false;
      if (opts.exclude?.includes(p.spec.id)) return false;
      if (opts.tier && p.spec.tier !== opts.tier) return false;
      if (opts.inputPorts && !p.spec.inputs.every((input) => opts.inputPorts!.includes(input))) {
        return false;
      }
      return true;
    });

    if (available.length === 0) return null;

    // Sort by tier: fast → standard → premium
    const tierOrder: ParserTier[] = ['fast', 'standard', 'premium'];
    available.sort((a, b) => tierOrder.indexOf(a.spec.tier) - tierOrder.indexOf(b.spec.tier));

    return available[0];
  }

  listSpecs(): ParserSpec[] {
    return [...this.parsers.values()].map((p) => p.spec);
  }

  /** Effect-native concurrent hook fan-out. Subscriber failures stay isolated. */
  dispatchHookEffect(
    hook: ParserHookName,
    ctx: HookContext,
    env: Record<string, unknown>,
  ): Effect.Effect<void> {
    const subscribers = [...this.parsers.values()].filter(
      (p) => p.spec.hooks?.includes(hook) && p.onHook && p.validate(env),
    );
    return Effect.all(
      subscribers.map((parser) =>
        Effect.tryPromise(() => parser.onHook!(ctx, env)).pipe(Effect.ignore),
      ),
      { concurrency: 'unbounded', discard: true },
    );
  }

  /** Fire-and-forget hook dispatch — never fails parsing. */
  dispatchHook(
    hook: ParserHookName,
    ctx: HookContext,
    env: Record<string, unknown>,
  ): Promise<void> {
    return Effect.runPromise(this.dispatchHookEffect(hook, ctx, env));
  }
}
